import { ReactNode, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import { App } from "antd";
import { DatasetStatus } from "types";
import {
  stopCreationStatusPolling,
  setCreationStatus,
  startCreationStatusPolling,
} from "redux/CreateDataset/actions";
import { fetchForAsyncDataPage } from "redux/SingleDataset/actions";
import { getDataset } from "redux/SingleDataset/selectors";
import { useFeatureFlagService } from "hooks/useFeatureFlagService";
import { getCreationStatus } from "redux/CreateDataset/selectors";
import useAllQueryParamsFilters from "hooks/useAllQueryParamsFilters";
import { useQueryClient } from "@tanstack/react-query";
import { invalidateActiveQueryByOperationId } from "vl-api/vlUtils";
import { useUserConfig } from "../../../hooks/useUserConfig";

function AddMediaCompletedModal() {
  const { datasetId } = useParams();
  const dispatch = useDispatch();
  const dataset = useSelector(getDataset);
  const creationStatus = useSelector(getCreationStatus);
  const [modalContent, setModalContent] = useState<ReactNode>(null);
  const wasUpdatingRef = useRef(false);
  const displayLPText = useFeatureFlagService("FLYWHEEL").show;
  const queryParamsFilters = useAllQueryParamsFilters();
  const queryClient = useQueryClient();
  const { refetch: refetchUserConfig } = useUserConfig();
  const { modal } = App.useApp();

  useEffect(() => {
    const shouldStartPolling =
      dataset?.is_updating || dataset?.status === DatasetStatus.READ_ONLY;
    if (shouldStartPolling) {
      wasUpdatingRef.current = true;

      if (datasetId) {
        dispatch(startCreationStatusPolling(datasetId) as any);
      }
    }

    return () => {
      dispatch(stopCreationStatusPolling() as any);
    };
  }, [dataset?.is_updating, dataset?.status, dispatch, datasetId]);

  useEffect(() => {
    const isReady = creationStatus?.status === DatasetStatus.READY;

    const isPendingIndex =
      creationStatus?.status === DatasetStatus.PARTIAL_INDEX;

    if (wasUpdatingRef.current && (isReady || isPendingIndex)) {
      if (isPendingIndex) {
        setModalContent(
          <>
            New data was added to your dataset and is ready to explore. <br />
            To use features like{" "}
            {displayLPText ? "label propagation" : "enrichment"}, you'll need to
            re-index the dataset.
            <br />
            you can do it now or wait if more data is on the way.
          </>,
        );
      } else if (isReady && creationStatus?.taskType === "SNAPSHOT_RESTORE") {
        setModalContent("Snapshot was restored successfully");
        invalidateActiveQueryByOperationId(
          queryClient,
          "listSnapshotsApiV1DatasetDatasetIdSnapshotGet",
        );
      }
      wasUpdatingRef.current = false;
      if (datasetId) {
        dispatch(fetchForAsyncDataPage(datasetId, queryParamsFilters) as any);
        dispatch(setCreationStatus(null));
        refetchUserConfig();
      }
    }
  }, [
    creationStatus,
    datasetId,
    dispatch,
    queryParamsFilters,
    displayLPText,
    queryClient,
    refetchUserConfig,
  ]);

  useEffect(() => {
    if (!modalContent) return;

    const instance = modal.success({
      title: "Good News!",
      content: modalContent,
      okText: "OK",
      closable: true,
      width: 500,
      autoFocusButton: null,
      afterClose: () => {
        setModalContent(null);
      },
    });

    return () => {
      instance.destroy();
    };
  }, [modalContent, modal]);

  return null;
}

export default AddMediaCompletedModal;
